<!--
  Promotion-record discount view (Story 1.6, §5.7). Renders inside the existing app shell.
  `%` only — never a `$`, list/unit/case price (BR-07). Agreed = read-only Story 1.5 primitive;
  New = editable %-only input while `draft`, read-only otherwise.
-->
<section class="pf-content" aria-labelledby="pf-discount-view-heading">
  <div class="pf-filter-bar pf-discount-header">
    <dx-button id="back-button" icon="arrowleft" [width]="32" [height]="32" hint="Back to promotions" (onClick)="back()" aria-label="Back to promotions"></dx-button>
    <h1 id="pf-discount-view-heading">Review Discounts</h1>
    <dx-button
      class="pf-save-button"
      type="default"
      stylingMode="outlined"
      icon="save"
      text="Save changes"
      [disabled]="!canSaveAll"
      [hint]="hasUnsavedChanges ? 'Save all changed discounts' : 'No unsaved discount changes'"
      (onClick)="saveAll()"
      aria-label="Save all changed discounts"
    ></dx-button>
    <dx-button
      class="pf-submit-button"
      type="default"
      icon="send"
      text="Submit to suppliers"
      [disabled]="!canSubmit || submitting"
      [hint]="canSubmit
        ? 'Submit the drafted discounts to the connected suppliers'
        : 'No drafted discounts to submit — every product in this promotion period is already submitted'"
      (onClick)="submitToSuppliers()"
      aria-label="Submit the drafted discounts to the connected suppliers"
    ></dx-button>
  </div>
  <p class="pf-page-description">Review and update the agreed and new discounts for a promotion record.</p>

  <p *ngIf="!loading && !loadError && rows.length === 0" class="pf-empty">
    No products with discounts were found for this promotion period.
  </p>

  <p *ngIf="loadError" class="pf-empty" role="alert">
    The discounts could not be loaded. Please go back and try again.
  </p>

  <div class="pf-table-wrap" *ngIf="!loading && rows.length > 0">
    <!--
      One grid row per (product, supplier), matching the rest of the app's dx-data-grid screens
      (cf. Customer Pricing). A single discount line per product — tiered pricing is resolved
      upstream via product_pricing / buying_group account_code, so there is no Pour / Back Bar split.
    -->
    <dx-data-grid
      [dataSource]="rows"
      keyExpr="id"
      [showBorders]="true"
      [columnAutoWidth]="true"
      [wordWrapEnabled]="true"
      [allowColumnReordering]="true"
      height="100%"
      aria-label="Promotion product discounts"
      (onToolbarPreparing)="onToolbarPreparing($event)"
    >
      <!-- Sorting, a toolbar search field, header-filter dropdowns and the column chooser, matching the
           rest of the app's grids (cf. Warehouse Pricing). The discount columns sort/filter on their
           primary (tier-1) value; the Actions column is excluded as it holds only controls. -->
      <dxo-sorting mode="multiple"></dxo-sorting>
      <dxo-search-panel [visible]="true"></dxo-search-panel>
      <dxo-header-filter [visible]="true"></dxo-header-filter>
      <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
      <!-- Persist column order/width/sort/filter/visibility per user in localStorage (cf. Warehouse Pricing). -->
      <dxo-state-storing [enabled]="true" type="localStorage" storageKey="promotionDiscountViewState"></dxo-state-storing>

      <dxi-column dataField="productCode" dataType="string" caption="Code" cssClass="pf-col-code" cellTemplate="codeCell"></dxi-column>
      <dxi-column dataField="productName" dataType="string" caption="Product" cssClass="pf-col-product" cellTemplate="productCell"></dxi-column>
      <dxi-column dataField="supplierName" dataType="string" caption="Supplier" cssClass="pf-col-supplier"></dxi-column>
      <dxi-column dataField="discountDollar" dataType="number" alignment="left" caption="Discount" cssClass="pf-col-discount" cellTemplate="discountCell"></dxi-column>
      <dxi-column dataField="agreed" dataType="number" alignment="left" caption="Agreed" cssClass="pf-col-discount" cellTemplate="agreedCell"></dxi-column>
      <dxi-column dataField="newDiscount" dataType="number" alignment="left" caption="New" cssClass="pf-col-discount" cellTemplate="newCell"></dxi-column>

      <div *dxTemplate="let cell of 'productCell'">
        <div class="pf-product-name">{{ cell.data.productName }}</div>
      </div>

      <div *dxTemplate="let cell of 'codeCell'">
        <span class="pf-product-code">{{ cell.data.productCode }}</span>
      </div>

      <!-- Discount: the raw `$` allowance (discount_dollar = allowance_group), one line per product.
           BR-07 override for THIS screen only. Editable `$` input while `draft` (editing it recalculates
           the New % via the conversion factor), read-only otherwise — null/missing renders an em-dash,
           never $0.00. -->
      <div *dxTemplate="let cell of 'discountCell'">
        <ng-container *ngIf="cell.data.isDraft; else readOnlyDiscount">
          <div class="pf-new-tier">
            <span class="pf-dollar-prefix" aria-hidden="true">$</span>
            <dx-number-box
              [value]="$any(cell.data.discountDollar)"
              [showSpinButtons]="false"
              format="#0.##"
              [inputAttr]="{ 'aria-label': 'Discount allowance, dollars' }"
              (onValueChanged)="onDiscountValueChanged(cell.data, $event)"
            ></dx-number-box>
          </div>
        </ng-container>
        <ng-template #readOnlyDiscount>
          <div class="pf-new-tier">
            <span>{{ formatDollar(cell.data.discountDollar) }}</span>
          </div>
        </ng-template>
      </div>

      <!-- Agreed (read-only, Story 1.5 primitive) — one line, % only (BR-07). -->
      <div *dxTemplate="let cell of 'agreedCell'">
        <app-discount-value-cell
          [tier1Value]="cell.data.agreed"
          valueContext="Agreed"
          [precision]="2"
        ></app-discount-value-cell>
      </div>

      <!-- New (proposed) — editable %-only input while `draft`, read-only otherwise. -->
      <div *dxTemplate="let cell of 'newCell'">
        <ng-container *ngIf="cell.data.isDraft; else readOnlyNew">
          <div class="pf-new-tier">
            <dx-number-box
              [value]="$any(cell.data.newDiscount)"
              [showSpinButtons]="false"
              format="#0.##"
              [inputAttr]="{ 'aria-label': 'Discount New, percent' }"
              (onValueChanged)="onNewValueChanged(cell.data, $event)"
            ></dx-number-box>
            <span class="pf-percent-suffix" aria-hidden="true">%</span>
            <div class="pf-field-error" role="alert" *ngIf="cell.data.error">{{ cell.data.error }}</div>
          </div>
        </ng-container>
        <ng-template #readOnlyNew>
          <app-discount-value-cell
            [tier1Value]="cell.data.newDiscount"
            valueContext="New"
            [precision]="2"
          ></app-discount-value-cell>
        </ng-template>
      </div>
    </dx-data-grid>
  </div>

  <dx-load-panel
    [(visible)]="loading"
    [showIndicator]="true"
    [showPane]="true"
    [shading]="true"
    [hideOnOutsideClick]="false"
    message="Loading discounts…"
  ></dx-load-panel>
</section>
